import type { IssueTrackingType } from "./useTodoList";
import type { NarrativeFlowChatUiAction } from "../../../../backend-hono/src/types/chat-ui-actions";

export interface ChatUiTodoItem {
  text: string;
  issueType?: IssueTrackingType;
  priority?: string;
}

export interface ChatUiRightRailPayload {
  title: string;
  surface?:
    | "plan"
    | "canvas"
    | "browser"
    | "report"
    | "narrativeflow"
    | "narrativeflow-data"
    | "narrativeflow-edit";
  markdown: string;
  append?: boolean;
  artifactId?: string;
  sourceAction?: "present_x_post";
}

export type NarrativeFlowUiAction = NarrativeFlowChatUiAction;

export interface NarrativeFlowUiActionPayload {
  action: NarrativeFlowUiAction;
  surface?: "workspace" | "forecasts" | "coliseum" | "resolved" | "map";
  dataKind?: string;
  editType?: string;
  sessionId?: string;
  targetId?: string;
  operationId?: string;
  title?: string;
  markdown?: string;
  previewMarkdown?: string;
  patch?: Record<string, unknown>;
  append?: boolean;
}

export interface ChatUiQuestion {
  id: string;
  label: string;
  question: string;
  placeholder?: string;
  required?: boolean;
}

export interface ChatUiQuestionnaire {
  actionId: string;
  title: string;
  summary?: string;
  questions: ChatUiQuestion[];
}

export type ChatUiPlanProposalStatus =
  "pending" | "approved" | "denied" | "expired";

export interface ChatUiPlanProposal {
  actionId: string;
  title: string;
  summary?: string;
  markdown: string;
  approvalLabel?: string;
  denyLabel?: string;
  surface?: "chat" | "narrativeflow";
  workspaceId?: string;
  status: ChatUiPlanProposalStatus;
  feedback?: string;
}

export interface ChatUiAnswer {
  id: string;
  label: string;
  value: string;
}

export interface ChatAnswerWidget {
  id: string;
  title: string;
  markdown: string;
  createdAt: number;
}

export interface UseChatUiActionsOptions {
  surfaceId?: string | null;
  workspaceId?: string | null;
  onTodoDrawer?: (payload: { title?: string; items: ChatUiTodoItem[] }) => void;
  onRightRail?: (payload: ChatUiRightRailPayload) => void;
  onNarrativeFlowAction?: (payload: NarrativeFlowUiActionPayload) => void;
  onQuestionnaireContinuation?: (message: string) => void;
  onPlanDecisionContinuation?: (
    message: string,
    status: Exclude<ChatUiPlanProposalStatus, "pending" | "expired">,
  ) => void;
}

export interface CognitionStep {
  kind?: string;
  label?: string;
  detail?: string;
  ts?: number;
}

interface BufferedCognitionStep {
  requestId: string;
  step: CognitionStep;
  receivedAt: number;
}

declare global {
  interface Window {
    __fintheonCognitionStepBuffer?: BufferedCognitionStep[];
  }
}

const COGNITION_STEP_BUFFER_TTL_MS = 30_000;
const COGNITION_STEP_BUFFER_LIMIT = 100;

function freshBufferedSteps(now: number): BufferedCognitionStep[] {
  return (window.__fintheonCognitionStepBuffer ?? []).filter(
    (entry) => now - entry.receivedAt <= COGNITION_STEP_BUFFER_TTL_MS,
  );
}

export function publishCognitionStep(requestId: string, step: CognitionStep) {
  if (typeof window === "undefined") return;
  const now = Date.now();
  const next = [
    ...freshBufferedSteps(now),
    { requestId, step, receivedAt: now },
  ];
  window.__fintheonCognitionStepBuffer = next.slice(
    -COGNITION_STEP_BUFFER_LIMIT,
  );
  window.dispatchEvent(
    new CustomEvent("fintheon:cognition-step", {
      detail: { requestId, step },
    }),
  );
}

export function replayBufferedCognitionSteps(
  requestId: string,
  handleStep: (step: CognitionStep) => void,
) {
  if (typeof window === "undefined") return;
  const fresh = freshBufferedSteps(Date.now()).slice(
    -COGNITION_STEP_BUFFER_LIMIT,
  );
  window.__fintheonCognitionStepBuffer = fresh;
  for (const entry of fresh) {
    if (entry.requestId === requestId) handleStep(entry.step);
  }
}

export function answersToMarkdown(
  questionnaire: ChatUiQuestionnaire,
  answers: ChatUiAnswer[],
): string {
  const lines = [`### ${questionnaire.title}`, ""];
  for (const answer of answers) {
    lines.push(`**${answer.label}**`);
    lines.push(answer.value || "_No answer provided._");
    lines.push("");
  }
  return lines.join("\n");
}

export function xPostToRail(
  detail: Record<string, unknown>,
): ChatUiRightRailPayload {
  const url = typeof detail.url === "string" ? detail.url : "";
  const title =
    typeof detail.title === "string" && detail.title.trim()
      ? detail.title.trim()
      : "X Post";
  const author =
    typeof detail.author === "string" && detail.author.trim()
      ? detail.author.trim()
      : null;
  const text =
    typeof detail.text === "string" && detail.text.trim()
      ? detail.text.trim()
      : null;
  const reason =
    typeof detail.reason === "string" && detail.reason.trim()
      ? detail.reason.trim()
      : null;
  const lines = [`### ${title}`, ""];
  if (author) lines.push(`**Author:** ${author}`, "");
  if (text) lines.push(text, "");
  if (reason) lines.push(`**Why Harper surfaced it:** ${reason}`, "");
  if (url) lines.push(`[Open source post](${url})`);
  return {
    title,
    surface: "browser",
    markdown: lines.join("\n"),
    sourceAction: "present_x_post",
    artifactId:
      typeof detail.artifactId === "string" ? detail.artifactId : undefined,
  };
}

export function dispatchNarrativeFlowAction(
  payload: NarrativeFlowUiActionPayload,
) {
  window.dispatchEvent(
    new CustomEvent("fintheon:narrative-agent-action", { detail: payload }),
  );
  if (payload.action === "narrativeflow_open_surface" && payload.surface) {
    window.dispatchEvent(
      new CustomEvent("fintheon:narrative-surface-change", {
        detail: {
          mode: payload.surface,
          sessionId: payload.sessionId ?? payload.targetId,
        },
      }),
    );
  }
}

export function debugMalformedChatUiAction(
  reason: string,
  step: CognitionStep,
) {
  console.debug("[useChatUiActions] Ignored malformed chat UI action", {
    reason,
    step,
  });
}
